// =============================================================================
// Lairo Status — Firestore-backed live store.
//
// Refactored to listen to normalized collections (settings/page, components,
// incidents, maintenances) rather than a single `status/live` document.
// Real-time updates from individual collections are unified into a single
// reactive store shape so the frontend rendering continues working.
// =============================================================================
import { useEffect, useState } from "react";
import { doc, onSnapshot, writeBatch, collection, runTransaction } from "firebase/firestore";
import { db } from "./firebase";
import { seedState, uid, genHistory } from "./model";

let cache = null;
const listeners = new Set();
let started = false;
let unsubscribers = [];

let pageLoaded = false;
let componentsLoaded = false;
let incidentsLoaded = false;
let maintenancesLoaded = false;

function emit() {
  listeners.forEach((l) => {
    try { l(cache); } catch (e) { /* listener errors shouldn't break the fan-out */ }
  });
}

const DAY = 86400000;

function toMillis(val) {
  if (!val) return 0;
  if (typeof val === "number") return isFinite(val) ? val : 0;
  if (typeof val === "string") {
    const ms = new Date(val).getTime();
    return isFinite(ms) ? ms : 0;
  }
  if (typeof val === "object") {
    if (typeof val.toMillis === "function") {
      const ms = val.toMillis();
      return isFinite(ms) ? ms : 0;
    }
    const sec = typeof val.seconds === "number" ? val.seconds : (typeof val._seconds === "number" ? val._seconds : null);
    const nano = typeof val.nanoseconds === "number" ? val.nanoseconds : (typeof val._nanoseconds === "number" ? val._nanoseconds : 0);
    if (sec !== null) {
      const ms = sec * 1000 + Math.floor(nano / 1000000);
      return isFinite(ms) ? ms : 0;
    }
    if (val instanceof Date) {
      const ms = val.getTime();
      return isFinite(ms) ? ms : 0;
    }
  }
  return 0;
}

function sumIntervals(intervals) {
  if (intervals.length === 0) return 0;
  // Sort by start time
  intervals.sort((a, b) => a[0] - b[0]);
  
  const merged = [intervals[0]];
  for (let i = 1; i < intervals.length; i++) {
    const next = intervals[i];
    const current = merged[merged.length - 1];
    
    if (next[0] <= current[1]) {
      current[1] = Math.max(current[1], next[1]);
    } else {
      merged.push(next);
    }
  }
  
  return merged.reduce((sum, [start, end]) => sum + (end - start), 0);
}

function computeComponentHistory(compId, incidents, maintenances) {
  const now = Date.now();
  const d0 = new Date(now);
  d0.setHours(0, 0, 0, 0);
  const today0 = d0.getTime();
  const days = [];

  const compIncidents = (incidents || []).filter(i => i.affected?.includes(compId));
  const compMaintenances = (maintenances || []).filter(m => m.affected?.includes(compId));

  for (let i = 89; i >= 0; i--) {
    const dateStart = today0 - i * DAY;
    const dateEnd = dateStart + DAY;

    let dayStatus = "operational";
    let totalOutageMs = 0;
    let worstImpact = "none";
    const dayIntervals = [];

    // 1. Check Incidents overlapping this day (capped at now to prevent future leaks)
    compIncidents.forEach(inc => {
      const start = toMillis(inc.createdAt);
      const resolvedUpdate = inc.updates?.find(u => u.status === "resolved");
      const end = toMillis(inc.resolvedAt || resolvedUpdate?.at || (inc.status !== "resolved" ? now : start));

      const overlapStart = Math.max(start, dateStart);
      const overlapEnd = Math.min(end, dateEnd, now);

      if (overlapStart < overlapEnd) {
        dayIntervals.push([overlapStart, overlapEnd]);
        const impact = inc.impact || "minor";
        if (impact === "critical" && worstImpact !== "critical") {
          worstImpact = "critical";
        } else if (impact === "major" && worstImpact !== "critical" && worstImpact !== "major") {
          worstImpact = "major";
        } else if (impact === "minor" && worstImpact === "none") {
          worstImpact = "minor";
        }
      }
    });

    totalOutageMs = sumIntervals(dayIntervals);

    // 2. Check Maintenances overlapping this day (capped at now to prevent future leaks)
    compMaintenances.forEach(m => {
      const start = toMillis(m.start);
      const end = toMillis(m.end);

      const overlapStart = Math.max(start, dateStart);
      const overlapEnd = Math.min(end, dateEnd, now);

      if (overlapStart < overlapEnd) {
        if (worstImpact === "none") {
          worstImpact = "maintenance";
        }
      }
    });

    if (worstImpact === "critical") {
      dayStatus = "major";
    } else if (worstImpact === "major") {
      dayStatus = "partial";
    } else if (worstImpact === "minor") {
      dayStatus = "degraded";
    } else if (worstImpact === "maintenance") {
      dayStatus = "maintenance";
    }

    let pct = 100;
    if (dayStatus !== "operational" && dayStatus !== "maintenance") {
      const outageRatio = totalOutageMs / DAY;
      pct = Math.max(0, Math.min(100, 100 - (outageRatio * 100)));
    }

    days.push({
      date: dateStart,
      status: dayStatus,
      pct: parseFloat(pct.toFixed(2))
    });
  }

  return days;
}

function emitIfReady() {
  if (pageLoaded && componentsLoaded && incidentsLoaded && maintenancesLoaded) {
    if (cache) {
      cache.components = (cache.components || []).map((c) => ({
        ...c,
        history: computeComponentHistory(c.id, cache.incidents, cache.maintenances)
      }));
    }
    emit();
  }
}

function handleError(err) {
  console.error("Firestore listener error:", err);
  cache = { __error: err.message || String(err) };
  emit();
}

// Lazily attach snapshots to all collections
function start() {
  if (started) return;
  started = true;

  cache = {
    page: { name: "", tagline: "" },
    announcement: { active: false, tone: "info", text: "" },
    components: [],
    incidents: [],
    maintenances: [],
    updatedAt: Date.now()
  };

  const unsubPage = onSnapshot(
    doc(db, "settings", "page"),
    (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        cache.page = { name: data.name || "", tagline: data.tagline || "" };
        cache.announcement = data.announcement || { active: false, tone: "info", text: "" };
        cache.updatedAt = data.updatedAt || Date.now();
        delete cache.__missing;
      } else {
        cache.__missing = true;
      }
      pageLoaded = true;
      emitIfReady();
    },
    handleError
  );

  const unsubComponents = onSnapshot(
    collection(db, "components"),
    (snap) => {
      cache.components = snap.docs.map(d => d.data());
      componentsLoaded = true;
      emitIfReady();
    },
    handleError
  );

  const unsubIncidents = onSnapshot(
    collection(db, "incidents"),
    (snap) => {
      cache.incidents = snap.docs.map(d => d.data());
      incidentsLoaded = true;
      emitIfReady();
    },
    handleError
  );

  const unsubMaintenances = onSnapshot(
    collection(db, "maintenances"),
    (snap) => {
      cache.maintenances = snap.docs.map(d => d.data());
      maintenancesLoaded = true;
      emitIfReady();
    },
    handleError
  );

  unsubscribers = [unsubPage, unsubComponents, unsubIncidents, unsubMaintenances];
}

export function subscribe(fn) {
  listeners.add(fn);
  start();
  if (cache && pageLoaded && componentsLoaded && incidentsLoaded && maintenancesLoaded) {
    fn(cache);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) {
      unsubscribers.forEach(unsub => unsub());
      unsubscribers = [];
      started = false;
      pageLoaded = false;
      componentsLoaded = false;
      incidentsLoaded = false;
      maintenancesLoaded = false;
      cache = null;
    }
  };
}

export function getState() {
  return cache;
}

// ---- Write Operations (Atomic / Transactions) ------------------------------

export async function createIncident({ title, impact, status, affected, compStatus, body, createdAt }) {
  const batch = writeBatch(db);
  const incId = uid("inc");
  const incRef = doc(db, "incidents", incId);
  const startTs = createdAt || Date.now();

  const newIncident = {
    id: incId,
    kind: "incident",
    title,
    impact,
    status,
    affected,
    createdAt: startTs,
    updates: [{ id: uid("u"), status, body, at: startTs }]
  };

  batch.set(incRef, newIncident);

  if (compStatus && compStatus !== "none" && affected?.length > 0) {
    affected.forEach((compId) => {
      batch.update(doc(db, "components", compId), { status: compStatus });
    });
  }

  batch.update(doc(db, "settings", "page"), { updatedAt: Date.now() });
  await batch.commit();
}

export async function updateIncident(id, { status, body, resolveComps, updatedAt }) {
  const incRef = doc(db, "incidents", id);
  const pageRef = doc(db, "settings", "page");
  const updateTs = updatedAt || Date.now();

  await runTransaction(db, async (transaction) => {
    const incSnap = await transaction.get(incRef);
    if (!incSnap.exists()) throw new Error("Incident not found");
    const inc = incSnap.data();

    inc.status = status;
    inc.updates.push({ id: uid("u"), status, body, at: updateTs });

    transaction.update(incRef, inc);

    const closing = (status === "resolved");
    if (closing && resolveComps && inc.affected?.length > 0) {
      inc.affected.forEach((compId) => {
        transaction.update(doc(db, "components", compId), { status: "operational" });
      });
    }

    transaction.update(pageRef, { updatedAt: Date.now() });
  });
}

export async function deleteIncident(id) {
  const batch = writeBatch(db);
  batch.delete(doc(db, "incidents", id));
  batch.update(doc(db, "settings", "page"), { updatedAt: Date.now() });
  await batch.commit();
}

export async function updateIncidentFields(id, { title, impact, createdAt, resolvedAt, updates }) {
  const incRef = doc(db, "incidents", id);
  const pageRef = doc(db, "settings", "page");

  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(incRef);
    if (!snap.exists()) throw new Error("Incident not found");
    const inc = snap.data();

    inc.title = title;
    inc.impact = impact;
    inc.createdAt = createdAt;
    inc.resolvedAt = resolvedAt || null;
    
    if (updates) {
      inc.updates = updates;
      // Sync incident status to match the latest update's status
      if (updates.length > 0) {
        inc.status = updates[updates.length - 1].status;
      }
    }

    transaction.update(incRef, inc);
    transaction.update(pageRef, { updatedAt: Date.now() });
  });
}

export async function createMaintenance({ title, start, end, body, affected, createdAt }) {
  const batch = writeBatch(db);
  const maintId = uid("mnt");
  const maintRef = doc(db, "maintenances", maintId);
  const startTs = createdAt || Date.now();

  const newMaint = {
    id: maintId,
    kind: "maintenance",
    title,
    status: "scheduled",
    affected,
    start,
    end,
    createdAt: Date.now(),
    updates: [{ id: uid("u"), status: "scheduled", body, at: Date.now() }]
  };

  batch.set(maintRef, newMaint);
  batch.update(doc(db, "settings", "page"), { updatedAt: Date.now() });
  await batch.commit();
}

export async function updateMaintenance(id, { status, body, resolveComps }) {
  const maintRef = doc(db, "maintenances", id);
  const pageRef = doc(db, "settings", "page");

  await runTransaction(db, async (transaction) => {
    const maintSnap = await transaction.get(maintRef);
    if (!maintSnap.exists()) throw new Error("Maintenance not found");
    const maint = maintSnap.data();

    maint.status = status;
    maint.updates.push({ id: uid("u"), status, body, at: Date.now() });

    transaction.update(maintRef, maint);

    const closing = (status === "completed");
    if (closing && resolveComps && maint.affected?.length > 0) {
      maint.affected.forEach((compId) => {
        transaction.update(doc(db, "components", compId), { status: "operational" });
      });
    }

    transaction.update(pageRef, { updatedAt: Date.now() });
  });
}

export async function deleteMaintenance(id) {
  const batch = writeBatch(db);
  batch.delete(doc(db, "maintenances", id));
  batch.update(doc(db, "settings", "page"), { updatedAt: Date.now() });
  await batch.commit();
}

export async function updateComponentStatus(id, status) {
  const batch = writeBatch(db);
  batch.update(doc(db, "components", id), { status });
  batch.update(doc(db, "settings", "page"), { updatedAt: Date.now() });
  await batch.commit();
}

export async function upsertComponent(component) {
  const batch = writeBatch(db);
  const id = component.id || uid("comp");
  const compRef = doc(db, "components", id);

  const data = {
    id,
    name: component.name,
    group: component.group || null,
    description: component.description || "",
    status: component.status || "operational",
    history: component.history || genHistory({}, Date.now())
  };

  batch.set(compRef, data, { merge: true });
  batch.update(doc(db, "settings", "page"), { updatedAt: Date.now() });
  await batch.commit();
}

export async function deleteComponent(id) {
  const batch = writeBatch(db);
  batch.delete(doc(db, "components", id));
  batch.update(doc(db, "settings", "page"), { updatedAt: Date.now() });
  await batch.commit();
}

export async function saveAnnouncement({ active, tone, text }) {
  const batch = writeBatch(db);
  batch.update(doc(db, "settings", "page"), {
    announcement: { active, tone, text },
    updatedAt: Date.now()
  });
  await batch.commit();
}

// Reset to seed data (admin-only via rules).
export async function reset() {
  const batch = writeBatch(db);

  if (cache) {
    if (cache.components) {
      cache.components.forEach(c => batch.delete(doc(db, "components", c.id)));
    }
    if (cache.incidents) {
      cache.incidents.forEach(i => batch.delete(doc(db, "incidents", i.id)));
    }
    if (cache.maintenances) {
      cache.maintenances.forEach(m => batch.delete(doc(db, "maintenances", m.id)));
    }
  }

  const next = seedState();

  batch.set(doc(db, "settings", "page"), {
    name: next.page.name,
    tagline: next.page.tagline,
    announcement: next.announcement,
    updatedAt: next.updatedAt
  });

  next.components.forEach(c => batch.set(doc(db, "components", c.id), c));
  next.incidents.forEach(i => batch.set(doc(db, "incidents", i.id), i));
  next.maintenances.forEach(m => batch.set(doc(db, "maintenances", m.id), m));

  await batch.commit();
}

export async function clearAll() {
  const batch = writeBatch(db);

  if (cache) {
    if (cache.incidents) {
      cache.incidents.forEach(i => batch.delete(doc(db, "incidents", i.id)));
    }
    if (cache.maintenances) {
      cache.maintenances.forEach(m => batch.delete(doc(db, "maintenances", m.id)));
    }
    if (cache.components) {
      cache.components.forEach(c => {
        batch.update(doc(db, "components", c.id), {
          status: "operational",
          history: genHistory({})
        });
      });
    }
  }

  batch.update(doc(db, "settings", "page"), {
    announcement: { active: false, tone: "info", text: "" },
    updatedAt: Date.now()
  });

  await batch.commit();
}

// ---- React hook ------------------------------------------------------------
export function useStore() {
  const [state, setState] = useState(() => cache);
  useEffect(() => subscribe((s) => setState(s ? { ...s } : s)), []);
  return state;
}
